// ausweisarten.ts — welche Ausweisdokumente erfasst werden können, und welche
// Felder jede Art zeigt.
//
// Der Scan erkennt die Art selbst (`ocr/personalausweis.schema.ts`, Feld
// `dokumentart`); hier steht dieselbe Auswahl für die Eingabe von Hand. Die
// Werte sind Speicherwerte und sollen sich nicht ändern — sie landen in
// `Contact.idCardType`.
//
// PO 23.09.2026: Die Maske heißt so, wie es auf dem Dokument steht — beim
// Führerschein nicht „Ausweisnummer". Und Führerschein ist nicht gleich
// Führerschein: Es gibt ihn für Fahrzeuge, für Boote und fürs Fliegen. Deshalb
// fragt die Maske bei manchen Arten erst nach der genaueren Art
// (`Contact.idCardSubtype`) und zeigt danach die passenden Klassen bzw. Scheine
// (`Contact.idCardClasses`).
//
// Rein, ohne React/DOM → in der vitest-Umgebung „node" testbar.

export const PERSONALAUSWEIS = "Personalausweis";
export const REISEPASS = "Reisepass";
export const AUFENTHALTSTITEL = "Aufenthaltstitel";
export const FUEHRERSCHEIN = "Führerschein";
export const SONSTIGES_AUSWEISDOKUMENT = "Sonstiges Ausweisdokument";
// Personenstandsurkunden (User 28.09.2026: „Geburtskarte, Sterbeurkunde
// hinzufügen"). „Geburtskarte" ist die Geburtsurkunde — kein eigenes Dokument,
// deshalb EINE Art. Beide haben keine Gültigkeit, dafür Registernummer und
// Standesamt.
export const GEBURTSURKUNDE = "Geburtsurkunde";
export const STERBEURKUNDE = "Sterbeurkunde";
// Heiratsurkunde (Plan 2026-09-30-ocr-scan-kontaktanlage, U3): Eheurkunde bzw.
// Auszug aus dem Eheregister — wie die anderen Urkunden ohne Gültigkeit.
export const HEIRATSURKUNDE = "Heiratsurkunde";

export const AUSWEIS_ARTEN: string[] = [
  PERSONALAUSWEIS,
  REISEPASS,
  AUFENTHALTSTITEL,
  FUEHRERSCHEIN,
  GEBURTSURKUNDE,
  HEIRATSURKUNDE,
  STERBEURKUNDE,
  SONSTIGES_AUSWEISDOKUMENT,
];

/**
 * Welcher Scan zu welcher Dokumentart gehört (Endpunkt `POST /api/ocr/<art>`).
 * Reisepass, Aufenthaltstitel und sonstige Ausweise laufen über den
 * Ausweis-Scan: Er liest Nummer, Daten und Behörde und erkennt die Art selbst.
 */
export type DokumentScanArt =
  | "personalausweis"
  | "fuehrerschein"
  | "geburtsurkunde"
  | "heiratsurkunde"
  | "sterbeurkunde";

export function scanArtFuer(art: string | null | undefined): DokumentScanArt {
  switch ((art ?? "").trim()) {
    case FUEHRERSCHEIN:
      return "fuehrerschein";
    case GEBURTSURKUNDE:
      return "geburtsurkunde";
    case HEIRATSURKUNDE:
      return "heiratsurkunde";
    case STERBEURKUNDE:
      return "sterbeurkunde";
    default:
      return "personalausweis";
  }
}

/** Die Rückfrage nach der genaueren Art — Auswahl oder freier Text. */
export type Unterart =
  | { frage: string; optionen: string[] }
  | { frage: string; optionen?: undefined; platzhalter: string };

/** Was die Maske für eine Dokumentart (und ggf. deren Unterart) zeigt. */
export interface DokumentFelder {
  /** Beschriftung von `idCardNumber`. */
  nummer: string;
  /** Beschriftung von `idCardAuthority` und deren Platzhalter. */
  behoerde: string;
  behoerdePlatzhalter: string;
  /** Beschriftung von `idCardValidUntil`. */
  gueltigBis: string;
  /** Mehrfachauswahl in `idCardClasses` — nur, wo das Dokument Klassen kennt. */
  klassen?: { label: string; optionen: string[] };
  /** Urkunden laufen nicht ab — „Gültig bis" entfällt. */
  ohneGueltigkeit?: boolean;
}

export const FUEHRERSCHEIN_KFZ = "Kraftfahrzeuge";
export const FUEHRERSCHEIN_BOOT = "Sportboot";
export const FUEHRERSCHEIN_LUFT = "Luftfahrt";

/** Die Rückfrage je Dokumentart. Arten ohne Eintrag fragen nicht nach. */
export const UNTERARTEN: Record<string, Unterart> = {
  [FUEHRERSCHEIN]: {
    frage: "Wofür gilt der Führerschein?",
    optionen: [FUEHRERSCHEIN_KFZ, FUEHRERSCHEIN_BOOT, FUEHRERSCHEIN_LUFT],
  },
  [AUFENTHALTSTITEL]: {
    frage: "Welcher Aufenthaltstitel?",
    optionen: [
      "Aufenthaltserlaubnis",
      "Niederlassungserlaubnis",
      "Erlaubnis zum Daueraufenthalt – EU",
      "Blaue Karte EU",
      "Visum",
      "Sonstiger Aufenthaltstitel",
    ],
  },
  [SONSTIGES_AUSWEISDOKUMENT]: {
    frage: "Welches Dokument?",
    platzhalter: "z. B. Schwerbehindertenausweis",
  },
};

const STANDARD: DokumentFelder = {
  nummer: "Ausweisnummer",
  behoerde: "Ausstellende Behörde",
  behoerdePlatzhalter: "Stadt Aachen",
  gueltigBis: "Gültig bis",
};

const FELDER: Record<string, DokumentFelder> = {
  [PERSONALAUSWEIS]: STANDARD,
  [REISEPASS]: { ...STANDARD, nummer: "Passnummer" },
  [AUFENTHALTSTITEL]: {
    ...STANDARD,
    nummer: "Dokumentnummer",
    behoerdePlatzhalter: "Ausländerbehörde Aachen",
  },
  [FUEHRERSCHEIN]: {
    ...STANDARD,
    nummer: "Führerscheinnummer",
    behoerdePlatzhalter: "Straßenverkehrsamt Aachen",
  },
  [SONSTIGES_AUSWEISDOKUMENT]: {
    ...STANDARD,
    nummer: "Dokumentnummer",
    behoerde: "Ausstellende Stelle",
    behoerdePlatzhalter: "",
  },
  [GEBURTSURKUNDE]: {
    ...STANDARD,
    nummer: "Registernummer",
    behoerde: "Standesamt",
    behoerdePlatzhalter: "Standesamt Aachen",
    ohneGueltigkeit: true,
  },
  [HEIRATSURKUNDE]: {
    ...STANDARD,
    nummer: "Registernummer",
    behoerde: "Standesamt",
    behoerdePlatzhalter: "Standesamt Aachen",
    ohneGueltigkeit: true,
  },
  [STERBEURKUNDE]: {
    ...STANDARD,
    nummer: "Registernummer",
    behoerde: "Standesamt",
    behoerdePlatzhalter: "Standesamt Aachen",
    ohneGueltigkeit: true,
  },
};

const FUEHRERSCHEIN_FELDER: Record<string, DokumentFelder> = {
  // EU-Kartenführerschein: Nummer in Feld 5, Klassen in Feld 9.
  [FUEHRERSCHEIN_KFZ]: {
    ...FELDER[FUEHRERSCHEIN],
    klassen: {
      label: "Fahrerlaubnisklassen",
      optionen: ["AM", "A1", "A2", "A", "B", "BE", "C1", "C1E", "C", "CE", "D1", "D1E", "D", "DE", "L", "T"],
    },
  },
  // Sportbootführerscheine sind unbefristet — „Gültig bis" nur für Ausnahmen.
  [FUEHRERSCHEIN_BOOT]: {
    ...FELDER[FUEHRERSCHEIN],
    behoerde: "Ausstellende Stelle",
    behoerdePlatzhalter: "DMYV / DSV",
    gueltigBis: "Gültig bis (falls befristet)",
    klassen: {
      label: "Scheine",
      optionen: [
        "SBF See",
        "SBF Binnen (Motor)",
        "SBF Binnen (Segel)",
        "Sportküstenschifferschein (SKS)",
        "Sportseeschifferschein (SSS)",
        "Sporthochseeschifferschein (SHS)",
        "UKW-Funkzeugnis (SRC)",
      ],
    },
  },
  [FUEHRERSCHEIN_LUFT]: {
    ...FELDER[FUEHRERSCHEIN],
    nummer: "Lizenznummer",
    behoerdePlatzhalter: "Luftfahrt-Bundesamt",
    klassen: {
      label: "Lizenzen und Berechtigungen",
      optionen: [
        "PPL(A) Flugzeug",
        "LAPL(A) Flugzeug",
        "PPL(H) Hubschrauber",
        "SPL Segelflug",
        "UL (Ultraleicht)",
        "BPL Ballon",
        "CPL",
        "ATPL",
        "Drohne A1/A3",
        "Drohne A2",
      ],
    },
  },
};

/**
 * Die Felder für Art und Unterart. Eine unbekannte Art — etwa ein Bestandswert
 * aus einem früheren Scan — bekommt die neutralen Beschriftungen.
 */
export function dokumentFelder(art: string | null | undefined, unterart?: string | null): DokumentFelder {
  const a = (art ?? "").trim();
  if (a === FUEHRERSCHEIN && unterart && FUEHRERSCHEIN_FELDER[unterart]) return FUEHRERSCHEIN_FELDER[unterart];
  return FELDER[a] ?? STANDARD;
}

/**
 * Die Klassen stehen als EIN Text in `idCardClasses`: „B, BE". Kein Wert
 * enthält ein Komma, und so bleibt die Spalte in Listen und Exporten lesbar.
 */
export function klassenLesen(wert: string | null | undefined): string[] {
  return (wert ?? "")
    .split(",")
    .map((k) => k.trim())
    .filter(Boolean);
}

/** Schreibt die Auswahl in der Reihenfolge des Katalogs — egal, wie geklickt wurde. */
export function klassenSchreiben(gewaehlt: string[], optionen: string[]): string {
  const set = new Set(gewaehlt);
  // Was der Katalog nicht (mehr) kennt, geht nicht still verloren.
  const fremd = gewaehlt.filter((g) => !optionen.includes(g));
  return [...optionen.filter((o) => set.has(o)), ...fremd].join(", ");
}

/** Eine Klasse an- oder abwählen und das Ergebnis als Speichertext liefern. */
export function klasseUmschalten(wert: string | null | undefined, klasse: string, optionen: string[]): string {
  const jetzt = klassenLesen(wert);
  const neu = jetzt.includes(klasse) ? jetzt.filter((k) => k !== klasse) : [...jetzt, klasse];
  return klassenSchreiben(neu, optionen);
}

// Flache Listen für `AusweisdokumentAbschnitt` (Kontaktanlage) — abgeleitet
// aus dem Katalog oben, damit es nur EINE Quelle gibt.
const optionenVon = (u: Unterart): string[] => u.optionen ?? [];
const klassenVon = (unterart: string): string[] =>
  dokumentFelder(FUEHRERSCHEIN, unterart).klassen?.optionen ?? [];

export const AUFENTHALTSTITEL_ARTEN: string[] = optionenVon(UNTERARTEN[AUFENTHALTSTITEL]);
export const FUEHRERSCHEIN_BEREICHE: string[] = optionenVon(UNTERARTEN[FUEHRERSCHEIN]);
export const KFZ_KLASSEN: string[] = klassenVon(FUEHRERSCHEIN_KFZ);
export const SPORTBOOT_SCHEINE: string[] = klassenVon(FUEHRERSCHEIN_BOOT);
export const LUFTFAHRT_LIZENZEN: string[] = klassenVon(FUEHRERSCHEIN_LUFT);

export const formatClassesInCatalogOrder = klassenSchreiben;
export const parseClassesString = klassenLesen;
